import React, { useEffect, useState } from "react";
import { Checkbox, Form, Modal, Progress, Select } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { HiOutlineVideoCamera } from "react-icons/hi2";
import { RxUpload } from "react-icons/rx";
import { FaBox } from "react-icons/fa";
import VideoRecorder from "../../../components/VideoRecorder/VideoRecorder";
import VideoPreview from "../../../components/VideoPreview/VideoPreview";

const { Option } = Select;

export const VideoModal = ({
  open,
  isRecording,
  videoRef,
  streamRef,
  stopRecording,
  setVideoModalVisible,
  setVideoPreview,
  setUploadedVideo,
}) => (
  <Modal
    open={open}
    onCancel={() => {
      if (videoRef.current) {
        const tracks = videoRef.current.srcObject?.getTracks();
        tracks?.forEach((track) => track.stop());
        videoRef.current.srcObject = null;
      }

      if (isRecording) {
        stopRecording();
      }

      setVideoModalVisible(false);
    }}
    maskClosable={false}
    destroyOnClose={true}
    afterClose={() => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
    }}
    footer={null}
    title={
      <div className="flex items-center gap-2">
        <HiOutlineVideoCamera className="text-xl" />
        <span>Record Video</span>
      </div>
    }
    width={600}
    className="video-recording-modal"
  >
    <VideoRecorder
      onVideoRecorded={(videoUrl) => {
        setVideoPreview(videoUrl);
        setUploadedVideo(videoUrl);
        setVideoModalVisible(false);
      }}
      maxDuration={180}
    />
  </Modal>
);

export const UploadProgressOverlay = ({ isUploading, uploadProgress }) =>
  isUploading && (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-xl w-96">
        <div className="text-center mb-4">
          <LoadingOutlined style={{ fontSize: 24 }} />
          <div className="mt-2">Uploading Video...</div>
        </div>
        <Progress percent={uploadProgress} status="active" />
      </div>
    </div>
  );

export const QuestionButtonConfig = ({ mainForm, questionNumber, buttonOptions }) => {
  const [isRequired, setIsRequired] = useState(true);

  useEffect(() => {
    const currentValues = mainForm.getFieldsValue();

    if (!currentValues.showButton && !currentValues.buttonType) {
      mainForm.setFieldsValue({
        showButton: true,
        buttonType: questionNumber === 0 ? "start" : "next",
      });
    }
  }, [mainForm, questionNumber]);

  return (
    <div className="mt-8 mb-8 w-full">
      <h3 className="text-md font-semibold m-0">Button Configuration</h3>
      <div className="flex flex-col gap-4">
        <Form.Item
          name="showButton"
          valuePropName="checked"
          initialValue={true}
          className="mb-0"
        >
          <Checkbox onChange={(e) => setIsRequired(e.target.checked)}>
            Show Button
          </Checkbox>
        </Form.Item>
        <div className="flex flex-col gap-1 w-[calc(50%-12px)]">
          <span>Button Text</span>
          <Form.Item
            name="buttonType"
            className="mb-0 min-w-[220px]"
            rules={[{ required: isRequired, message: "Please select button text" }]}
          >
            <Select
              disabled={!isRequired}
              placeholder="Select button text"
              className="w-full h-11"
            >
              {buttonOptions.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </div>
      </div>
    </div>
  );
};

export const HeaderActions = ({
  videoPreview,
  isVideoLoading = false,
  isUploading = false,
  handleVideoDelete,
  handleFileUpload,
  setVideoModalVisible,
  setBucketModalVisible,
  maxVideoUploadMb = 150,
}) => (
  <div className="border border-secondary rounded-xl p-3 min-h-[27px] flex flex-col items-center justify-center gap-3">
    {isVideoLoading ? (
      <div className="w-full h-[220px] rounded-xl bg-gray-100 animate-pulse" />
    ) : videoPreview ? (
      <div>
        <VideoPreview videoUrl={videoPreview} onDelete={handleVideoDelete} />
      </div>
    ) : (
      <>
        <div className="flex flex-wrap items-center justify-center gap-4">
          <button
            type="button"
            className="bg-secondary h-10 border-0 px-6 rounded-xl flex items-center gap-2"
            onClick={() =>
              document.getElementById("question-video-upload").click()
            }
            disabled={isUploading}
          >
            {isUploading ? (
              <LoadingOutlined className="text-lg" />
            ) : (
              <RxUpload className="text-lg" />
            )}
            Upload a Video
          </button>
          <input
            type="file"
            id="question-video-upload"
            accept="video/*"
            style={{ display: "none" }}
            onChange={handleFileUpload}
          />
          <span className="text-gray-500">or</span>
          <button
            type="button"
            className="bg-secondary h-9 border-0 px-6 rounded-xl flex items-center gap-2 m-0"
            onClick={() => setVideoModalVisible(true)}
          >
            <HiOutlineVideoCamera className="text-lg" />
            Record a BSL Video
          </button>
          <span className="text-gray-500">or</span>
          <button
            type="button"
            className="bg-secondary h-9 border-0 px-6 rounded-xl flex items-center gap-2"
            onClick={() => setBucketModalVisible(true)}
          >
            <FaBox className="text-lg" />
            Pick From Bucket
          </button>
        </div>
        <p className="mb-0 text-xs text-gray-500">
          Uploaded files: max {maxVideoUploadMb} MB
        </p>
      </>
    )}
  </div>
);
